/**
 * Утилиты для работы с продуктами: категории и единицы измерения.
 * Гарантирует, что у каждого продукта есть единица измерения (никогда null).
 */

/** Допустимые значения enum product_category в Supabase */
export const PRODUCT_CATEGORY_VALUES = [
  'vegetables',
  'fruits',
  'dairy',
  'meat',
  'grains',
  'other',
] as const;

export type ProductCategory = (typeof PRODUCT_CATEGORY_VALUES)[number];

/**
 * Приводит строку категории к значению enum product_category.
 * Невалидные значения заменяются на "other".
 */
export function ensureProductCategory(category: string | null | undefined): ProductCategory {
  if (!category || typeof category !== 'string') return 'other';
  const lower = category.toLowerCase().trim();
  if (PRODUCT_CATEGORY_VALUES.includes(lower as ProductCategory)) return lower as ProductCategory;
  return 'other';
}

/** Продукты, измеряемые в штуках */
const COUNTABLE = [
  'яблоко', 'яблок', 'груша', 'банан', 'апельсин', 'мандарин', 'лимон', 'лайм',
  'персик', 'абрикос', 'слива', 'киви', 'авокадо', 'гранат', 'хурма', 'инжир',
  'финик', 'помело', 'грейпфрут', 'нектарин', 'айва', 'яйцо', 'яйц',
  'картофель', 'картошка', 'лук', 'головка лука', 'чеснок', 'зубчик', 'огурец',
  'помидор', 'томат', 'перец', 'кабачок', 'баклажан', 'тыква', 'морковь',
  'свекла', 'редис', 'редиска', 'капуста', 'кочан', 'лайм', 'луковица',
  'булка', 'батон', 'бублик', 'сушка', 'вафл', 'пряник', 'печенье', 'крекер',
  'котлета', 'тефтел', 'оладь', 'блин', 'шашлык', 'стейк', 'шницель',
  'вареник', 'пельмен', 'манты', 'чебурек', 'пирожок', 'пирог', 'рулет',
  'бутерброд', 'сэндвич', 'тост', 'гренк', 'сухарь', 'хлебец', 'хлебцы',
  'кусок', 'ломтик', 'долька', 'дольки', 'половинка', 'четвертинка',
  'ветчина', 'колбаса', 'сосиск', 'сардел', 'рулет',
];

/** Продукты, измеряемые в объёме (мл) */
const VOLUME = [
  'молоко', 'кефир', 'йогурт', 'ряженка', 'простокваша', 'сливки', 'сметана',
  'вода', 'сок', 'бульон', 'масло растительное', 'масло оливковое', 'подсолнечное',
  'уксус', 'вино', 'пиво', 'компот', 'морс', 'кисель', 'отвар',
  'маринад', 'соус', 'растительное масло', 'оливковое масло',
  'жидкость', 'настой', 'сироп', 'мёд жидкий',
];

/** Продукты, измеряемые по весу (г). Остальное — по умолчанию "шт". */
const WEIGHT = [
  'мука', 'сахар', 'соль', 'перец молотый', 'специи', 'крупа', 'рис', 'гречка',
  'овсянка', 'пшено', 'манка', 'перловка', 'булгур', 'киноа', 'чечевица', 'нут',
  'фасоль', 'горох', 'бобы', 'макарон', 'вермишель', 'лапша', 'спагетти',
  'мясо', 'говядина', 'свинина', 'куриц', 'индейк', 'фарш', 'рыба', 'лосось',
  'треска', 'минтай', 'творог', 'сыр', 'брынза', 'моцарелла', 'пармезан',
  'орех', 'миндал', 'грецкий', 'фундук', 'кешью', 'арахис', 'семечк', 'семена',
  'изюм', 'курага', 'чернослив', 'сухофрукт', 'мед', 'мёд', 'варенье', 'джем',
  'шоколад', 'какао', 'пудра', 'крахмал', 'разрыхлитель', 'сода', 'дрожжи',
  'зелень', 'петрушка', 'укроп', 'базилик', 'кинза', 'сельдерей', 'шпинат',
  'капуста тушеная', 'икра', 'паста', 'паштет', 'пюре', 'пюре из', 'хлопья',
  'отруби', 'клетчатка', 'протеин', 'порошок', 'пудра сахарная',
  'масло сливочное', 'сливочное масло', 'маргарин',
];

/**
 * Определяет категорию продукта по названию (для списка покупок и т.д.)
 */
export function detectCategory(name: string): string {
  const lowerName = name.toLowerCase().trim();

  const vegetables = [
    'морковь', 'картофель', 'картошка', 'лук', 'чеснок', 'капуста', 'брокколи',
    'цветная капуста', 'кабачок', 'баклажан', 'перец', 'томат', 'помидор', 'огурец', 'свекла',
    'тыква', 'тыквенное', 'шпинат', 'салат', 'петрушка', 'укроп', 'сельдерей', 'редис', 'горох', 'фасоль',
    'зелень', 'базилик', 'мята', 'кинза', 'зеленый лук', 'порей', 'цуккини',
    'патиссон', 'брюссельская капуста', 'кольраби', 'репа', 'брюква', 'дайкон', 'редиска',
    'руккола', 'кресс-салат', 'щавель', 'ревень', 'спаржа', 'артишок',
  ];
  if (vegetables.some((v) => lowerName.includes(v))) return 'vegetables';

  const fruits = [
    'яблоко', 'яблок', 'яблочное', 'груша', 'банан', 'апельсин', 'мандарин', 'лимон', 'лайм',
    'виноград', 'клубника', 'малина', 'черника', 'голубика', 'смородина', 'вишня', 'черешня',
    'персик', 'абрикос', 'слива', 'манго', 'ананас', 'киви', 'дыня', 'арбуз', 'гранат',
    'хурма', 'инжир', 'финик', 'курага', 'изюм', 'чернослив', 'ягод', 'фрукт', 'помело',
    'грейпфрут', 'папайя', 'маракуйя', 'личи', 'рамбутан', 'дуриан', 'кокос',
    'фейхоа', 'нектарин', 'айва', 'крыжовник', 'облепиха', 'жимолость',
    'барбарис', 'рябина', 'калина', 'брусника', 'клюква', 'авокадо',
  ];
  if (fruits.some((f) => lowerName.includes(f))) return 'fruits';

  const dairy = [
    'молоко', 'кефир', 'йогурт', 'сметана', 'творог', 'сыр', 'масло сливочное',
    'сливки', 'ряженка', 'простокваша', 'брынза', 'моцарелла', 'пармезан', 'молочн',
    'творожок', 'сырок', 'сметанка', 'кефирчик', 'варенец', 'айран', 'тан', 'катык', 'кумыс', 'мацони', 'лактоза', 'сливочное масло',
  ];
  if (dairy.some((d) => lowerName.includes(d))) return 'dairy';

  const meat = [
    'мясо', 'говядина', 'свинина', 'свиная', 'свиной', 'свиное', 'вырезка свин', 'свиная вырезка',
    'курица', 'куриц', 'индейка', 'кролик', 'баранина', 'говяжья', 'говяжий', 'телятина',
    'фарш', 'колбаса', 'сосиски', 'ветчина', 'бекон', 'печень', 'сердце', 'язык',
    'рыба', 'лосось', 'семга', 'форель', 'треска', 'минтай', 'скумбрия', 'сельдь', 'тунец',
    'креветки', 'кальмар', 'морепродукт', 'филе', 'грудка', 'бедро', 'крыло', 'окорок',
    'козлятина', 'оленина', 'конина', 'утка', 'гусь', 'перепелка',
    'цесарка', 'фазан', 'краб', 'мидии', 'устрицы', 'осьминог', 'каракатица', 'икра',
  ];
  if (meat.some((m) => lowerName.includes(m))) return 'meat';

  const grains = [
    'рис', 'гречка', 'гречневая', 'овсянка', 'овсяные', 'пшено', 'перловка',
    'манка', 'манная', 'кускус', 'булгур', 'киноа', 'крупа', 'хлопья', 'мука', 'макароны',
    'спагетти', 'лапша', 'вермишель', 'хлеб', 'батон', 'булка', 'сухари', 'кукурузн',
    'пшеница', 'рожь', 'ячмень', 'овес', 'чечевица', 'нут', 'бобы', 'соя', 'тофу', 'сейтан', 'хлебцы', 'крекеры', 'печенье', 'вафли', 'пряники', 'сушки', 'баранки', 'бублики',
  ];
  if (grains.some((g) => lowerName.includes(g))) return 'grains';

  return 'other';
}

/**
 * Определяет единицу измерения продукта по названию.
 * Всегда возвращает непустую строку (шт / г / мл). Продуктов без меры не должно быть.
 */
export function detectUnit(name: string): string {
  if (!name || typeof name !== 'string') return 'шт';
  const lower = name.toLowerCase().trim();

  for (const w of VOLUME) {
    if (lower.includes(w)) return 'мл';
  }
  for (const w of WEIGHT) {
    if (lower.includes(w)) return 'г';
  }
  for (const w of COUNTABLE) {
    if (lower.includes(w)) return 'шт';
  }

  return 'шт';
}

/**
 * Возвращает единицу измерения: переданную (если задана) или определённую по названию.
 * Использовать при сохранении продукта/ингредиента, чтобы никогда не хранить null.
 */
export function resolveUnit(unit: string | null | undefined, productName: string): string {
  const u = typeof unit === 'string' ? unit.trim() : '';
  return u || detectUnit(productName);
}

/**
 * Невозможно применить числовое значение (г/мл) — использовать «шт» с количеством.
 * Для пюре, творога и т.п. при amount null/0 при создании и слиянии считаем штуками.
 */
export function usePiecesFallback(
  unit: string | null | undefined,
  amount: number | null | undefined
): boolean {
  const u = typeof unit === 'string' ? unit.trim().toLowerCase() : '';
  const isWeightOrVolume = u === 'г' || u === 'мл' || u === 'кг' || u === 'л';
  const noAmount = amount == null || Number(amount) === 0;
  return !!(isWeightOrVolume && noAmount);
}

/**
 * Продукты с описаниями-альтернативами или дробными мерами в названии:
 * «Вода или детский сок (яблочный/грушевый) — 2-3 ст.л.», «Желатин — 5 г (или агар-агар — 2 г)».
 * Добавлять как 1 шт, 2 шт и т.д.
 */
export function shouldUsePiecesByDescription(name: string): boolean {
  if (!name || typeof name !== 'string') return false;
  const s = name.trim();
  if (s.includes(' или ')) return true;
  if (s.includes('(или ')) return true;
  const hasDash = s.includes('—') || s.includes('–');
  const hasMeasure = /ст\.л\.|ч\.л\.|г\s*\)|мл\s*\)|\sг\b|\sмл\b/.test(s);
  return !!(hasDash && hasMeasure);
}

/**
 * Краткая форма единицы для отображения (например, «щепотка» → «щеп.»).
 */
export function formatUnitForDisplay(unit: string | null | undefined): string {
  if (!unit || typeof unit !== 'string') return '';
  const u = unit.trim().toLowerCase();
  if (u === 'щепотка' || u === 'щепотки' || u === 'щепотку') return 'щеп.';
  return unit.trim();
}

/**
 * Форматирует «количество + единица» для отображения в списке покупок.
 * Всегда показывает число, когда оно есть (2 банана → «2 шт»); «щепотка» → «щеп.».
 */
export function formatAmountUnit(
  amount: number | null | undefined,
  unit: string | null | undefined
): string {
  const u = formatUnitForDisplay(unit);
  if (!u && (amount == null || amount === '')) return '';
  if (amount != null && amount !== '' && !Number.isNaN(Number(amount))) {
    const n = Number(amount);
    const str = n % 1 === 0 ? String(Math.round(n)) : String(n);
    return u ? `${str} ${u}` : str;
  }
  return u || '';
}
